import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ClaimFile, listClaimFiles, uploadClaimFile, deleteClaimFile, Audit, Claim, EmailCorrespondence, PhoneCorrespondence, Correspondence, listLocDocuments, LocBlobDocument } from '../api/claims';
import { useAuth } from '../context/AuthContext';
import EmailViewer from './EmailViewer';
import CallRecorderModal from './CallRecorderModal';
import CorrespondenceViewer from './CorrespondenceViewer';
import PDFViewerDrawer from './PDFViewerDrawer';
import DocViewerDrawer from './DocViewerDrawer';
import { CallData } from './CallRecorder';
import './ClaimFiles.css';

interface ClaimFilesProps {
  claimId: string;
  claim?: Claim;
  audits?: Audit[];
  onFileCountChange?: (count: number) => void;
  correspondence?: Correspondence[];
  onAddCorrespondence?: (correspondence: Correspondence) => void;
  onDocViewerToggle?: (isOpen: boolean) => void;
}

export default function ClaimFiles({ claimId, claim, audits, onFileCountChange, correspondence, onAddCorrespondence, onDocViewerToggle }: ClaimFilesProps) {
  // Debug: Log correspondence data
  console.log('🔍 ClaimFiles received:', {
    claimId,
    hasCorrespondence: !!correspondence,
    correspondenceLength: correspondence?.length,
    correspondenceType: typeof correspondence,
    claimHasCorrespondence: !!claim?.correspondence,
    claimCorrespondenceLength: claim?.correspondence?.length
  });

  const [files, setFiles] = useState<ClaimFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [selectedEmail, setSelectedEmail] = useState<EmailCorrespondence | null>(null);
  const [showCallRecorder, setShowCallRecorder] = useState(false);
  const [showCorrespondenceViewer, setShowCorrespondenceViewer] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState<{ url: string; fileName: string } | null>(null);
  const [pdfOpenedFromCorrespondence, setPdfOpenedFromCorrespondence] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadUnits, setUploadUnits] = useState<string>('1');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadSender, setUploadSender] = useState('');
  const [uploadRecipient, setUploadRecipient] = useState('');
  const [uploadDate, setUploadDate] = useState('');
  const [uploadDirection, setUploadDirection] = useState<'sent' | 'received'>('received');
  const [isEmailFile, setIsEmailFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [locDropdownOpen, setLocDropdownOpen] = useState(false);
  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<{ url: string; fileName: string } | null>(null);

  // NOTE: We intentionally do NOT ask the parent to hide the claim drawer when
  // the internal PDF/Doc viewers open. The PDFViewerDrawer (zIndex 11000) and
  // DocViewerDrawer render as MUI Drawers with their own backdrop ABOVE the
  // claim drawer (zIndex 9999), so they overlay it cleanly. Hiding the claim
  // drawer unmounts ClaimFiles (and the viewer rendered inside it) the instant
  // it opens, which made PDFs "just close". Keeping the callback wired (as a
  // no-op of false) so the parent never hides the drawer for these viewers.
  useEffect(() => {
    onDocViewerToggle?.(false);
  }, [docViewerOpen, pdfViewerOpen, onDocViewerToggle]);

  // Fetch LOC documents from blob storage
  const [locDocuments, setLocDocuments] = useState<LocBlobDocument[]>([]);

  useEffect(() => {
    if (!claimId) return;
    listLocDocuments(claimId)
      .then(res => setLocDocuments(res.documents))
      .catch(err => console.error('Failed to fetch LOC documents:', err));
  }, [claimId]);

  // Open an LOC document (blob storage URLs already have fresh SAS tokens)
  const handleOpenLocDocument = useCallback((locDoc: LocBlobDocument) => {
    setLocDropdownOpen(false);
    const fileName = locDoc.blobName || 'Letter-of-Complaint.docx';
    if (fileName.toLowerCase().endsWith('.pdf')) {
      setSelectedPdf({ url: locDoc.url, fileName });
      setPdfViewerOpen(true);
    } else {
      setSelectedDoc({ url: locDoc.url, fileName });
      setDocViewerOpen(true);
    }
  }, []);

  // Close LOC dropdown when clicking outside
  useEffect(() => {
    if (!locDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.loc-dropdown-container')) {
        setLocDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [locDropdownOpen]);

  // Handle PDF open from correspondence viewer
  const handlePdfOpenFromCorrespondence = (url: string, fileName: string) => {
    setShowCorrespondenceViewer(false);
    setSelectedPdf({ url, fileName });
    setPdfViewerOpen(true);
    setPdfOpenedFromCorrespondence(true);
  };

  // Handle PDF close - reopen correspondence if needed
  const handlePdfClose = () => {
    setPdfViewerOpen(false);
    setSelectedPdf(null);
    if (pdfOpenedFromCorrespondence) {
      setShowCorrespondenceViewer(true);
      setPdfOpenedFromCorrespondence(false);
    }
  };
  
  const { isAdmin } = useAuth();
  
  // We need to get the current user's email from the claims response or store it in AuthContext
  // For now, we'll parse it from the JWT token if available
  const getUserEmail = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.email;
      } catch {
        return null;
      }
    }
    return null;
  };
  
  const currentUserEmail = getUserEmail();
  
  // Get user's full name from JWT token
  const getUserFullName = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Try different possible fields for the user's name
        if (payload.name) return payload.name;
        if (payload.fullName) return payload.fullName;
        if (payload.firstName && payload.lastName) {
          return `${payload.firstName} ${payload.lastName}`;
        }
        if (payload.firstName) return payload.firstName;
        if (payload.email) return payload.email;
        return 'Agent';
      } catch {
        return 'Agent';
      }
    }
    return 'Agent';
  };

  // Get user's first and last name separately for GCS search
  const getUserName = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          firstName: payload.firstName || payload.given_name || '',
          lastName: payload.lastName || payload.family_name || payload.surname || ''
        };
      } catch {
        return { firstName: '', lastName: '' };
      }
    }
    return { firstName: '', lastName: '' };
  };

  const currentUserName = getUserFullName();
  const { firstName: userFirstname, lastName: userLastname } = getUserName();

  // Load files when component mounts or claimId changes
  useEffect(() => {
    loadFiles();
  }, [claimId]);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listClaimFiles(claimId);
      setFiles(response.files);
      onFileCountChange?.(response.count);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const promptForUpload = (filesToUpload: FileList | File[]) => {
    const filesArray = Array.from(filesToUpload);
    setPendingFiles(filesArray);
    setUploadUnits('1');
    setUploadDescription('');
    setUploadSender('');
    setUploadRecipient('');
    setUploadDate('');
    setUploadDirection('received');

    // Check if all files are email files (.eml, .html, .htm)
    const allEmailFiles = filesArray.every(f => {
      const ext = (f.name || '').toLowerCase();
      return ext.endsWith('.eml') || ext.endsWith('.msg') || ext.endsWith('.html') || ext.endsWith('.htm');
    });
    setIsEmailFile(allEmailFiles);
    setShowUploadDialog(true);
  };

  const handleUploadConfirm = async () => {
    const units = parseInt(uploadUnits, 10);
    if (isNaN(units) || units < 0) {
      setError('Units must be a non-negative number');
      return;
    }
    if (!uploadDescription.trim()) {
      setError('Description is required');
      return;
    }

    setShowUploadDialog(false);
    setUploading(true);
    setError(null);

    const metadata: any = {
      units,
      description: uploadDescription.trim(),
      direction: uploadDirection,
    };

    // For non-email files, include user-provided metadata
    if (!isEmailFile) {
      if (uploadSender.trim()) metadata.sender = uploadSender.trim();
      if (uploadRecipient.trim()) metadata.recipient = uploadRecipient.trim();
      if (uploadDate) {
        const parsedDate = new Date(uploadDate);
        if (!isNaN(parsedDate.getTime())) {
          metadata.email_date = parsedDate.toISOString();
        }
      }
    }

    for (const file of pendingFiles) {
      try {
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

        const progressInterval = setInterval(() => {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: Math.min((prev[file.name] || 0) + 20, 90)
          }));
        }, 200);

        await uploadClaimFile(claimId, file, metadata);

        clearInterval(progressInterval);
        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));

        setTimeout(() => {
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[file.name];
            return newProgress;
          });
        }, 1000);

      } catch (err: any) {
        setError(`Failed to upload ${file.name}: ${err.response?.data?.error || err.message}`);
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
      }
    }

    setPendingFiles([]);
    setUploading(false);
    loadFiles();
  };

  const handleUploadCancel = () => {
    setShowUploadDialog(false);
    setPendingFiles([]);
  };

  const handleDelete = async (file: ClaimFile) => {
    if (!window.confirm(`Are you sure you want to delete ${file.filename}?`)) {
      return;
    }
    
    try {
      await deleteClaimFile(claimId, file.blob_name);
      loadFiles(); // Reload the file list
    } catch (err: any) {
      setError(`Failed to delete file: ${err.response?.data?.error || err.message}`);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set dragging to false if we're leaving the drop zone entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      promptForUpload(files);
    }
  }, [claimId]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Unknown date';
      return date.toLocaleString();
    } catch {
      return 'Unknown date';
    }
  };

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return '🖼️';
    if (contentType === 'application/pdf') return '📄';
    if (contentType.startsWith('application/vnd.ms-excel') || contentType.includes('spreadsheet')) return '📊';
    if (contentType.startsWith('application/vnd.ms-word') || contentType.includes('document')) return '📝';
    if (contentType.startsWith('text/')) return '📃';
    return '📎';
  };

  const handleSaveCall = async (callData: CallData) => {
    try {
      const newPhoneCorrespondence: PhoneCorrespondence = {
        id: `phone-${Date.now()}`, // Temporary ID, backend should generate real ID
        type: 'phone',
        direction: callData.direction,
        subject: callData.subject,
        from: callData.from,
        to: callData.to,
        timestamp: new Date().toISOString(),
        callDuration: callData.callDuration,
        callAnswered: callData.callAnswered,
        callNotes: callData.callNotes,
        bodyPreview: callData.callNotes.substring(0, 200),
        units: callData.units
      };

      // Call the parent component's handler if provided
      if (onAddCorrespondence) {
        onAddCorrespondence(newPhoneCorrespondence);
      }

      setShowCallRecorder(false);
    } catch (err: any) {
      setError('Failed to save call record');
    }
  };

  const handleViewFile = (file: ClaimFile) => {
    if (file.content_type === 'application/pdf') {
      setSelectedPdf({ url: file.url, fileName: file.filename });
      setPdfViewerOpen(true);
    } else {
      // For non-PDF files, open in new tab as before
      window.open(file.url, '_blank');
    }
  };

  const handleViewAuditDocument = (url: string, fileName: string) => {
    if (url.toLowerCase().endsWith('.pdf')) {
      setSelectedPdf({ url, fileName });
      setPdfViewerOpen(true);
    } else {
      window.open(url, '_blank');
    }
  };

  return (
    <div className="claim-files-container">
      <div className="files-header">
        <h3>Files & Correspondence</h3>
        <button 
          className="upload-button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          + Upload File
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div 
        className={`drop-zone ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="drop-zone-content">
          <span className="drop-icon">📁</span>
          <p>Drag and drop files here or click to upload</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => { if (e.target.files && e.target.files.length > 0) { promptForUpload(e.target.files); e.target.value = ''; } }}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {Object.entries(uploadProgress).map(([filename, progress]) => (
        <div key={filename} className="upload-progress-item">
          <div className="progress-info">
            <span>{filename}</span>
            <span>{progress}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      ))}

      {/* Display Audit Documents */}
      {audits && audits.length > 0 && (
        <div className="files-section">
          <h4>Audit Documents</h4>
          <ul className="files-list">
            {audits.map(audit => (
              audit.documentUrl && (
                <li key={audit.id} className="file-item">
                  <div className="file-icon">📄</div>
                  <div className="file-info">
                    <button
                      onClick={() => handleViewAuditDocument(
                        audit.documentUrl || '',
                        `Audit Document (${new Date(audit.auditedAt).toLocaleDateString()})`
                      )}
                      className="file-name"
                      style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                    >
                      Audit Document ({new Date(audit.auditedAt).toLocaleDateString()})
                    </button>
                    <span className="file-meta">
                      Generated on: {audit.documentGeneratedAt ? new Date(audit.documentGeneratedAt).toLocaleDateString() : 'N/A'}
                    </span>
                     <span className="file-meta">Audited by: {audit.auditedBy}</span>
                  </div>
                  <button
                    onClick={() => handleViewAuditDocument(
                      audit.documentUrl || '',
                      `Audit Document (${new Date(audit.auditedAt).toLocaleDateString()})`
                    )}
                    className="file-action-btn"
                  >
                    View
                  </button>
                </li>
              )
            ))}
          </ul>
        </div>
      )}

      {/* LOC Documents Section */}
      {locDocuments.length > 0 && (
        <div className="files-section">
          <h4>Letter of Complaint</h4>
          <div className="loc-documents-section">
            {locDocuments.length === 1 ? (
              <div className="loc-single-doc">
                <div className="file-icon">📧</div>
                <div className="loc-doc-info">
                  <strong>Letter of Complaint</strong>
                  <p className="loc-doc-date">
                    {locDocuments[0].lastModified
                      ? new Date(locDocuments[0].lastModified).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })
                      : 'Date unknown'}
                  </p>
                </div>
                <button
                  className="loc-view-btn"
                  onClick={() => handleOpenLocDocument(locDocuments[0])}
                >
                  View Document
                </button>
              </div>
            ) : (
              <div className="loc-multi-doc">
                <div className="file-icon">📧</div>
                <div className="loc-doc-info">
                  <strong>Letters of Complaint</strong>
                  <p className="loc-doc-date">
                    {locDocuments.length} documents available
                  </p>
                </div>
                <div className="loc-dropdown-container">
                  <button
                    className="loc-view-btn loc-dropdown-toggle"
                    onClick={() => setLocDropdownOpen(!locDropdownOpen)}
                  >
                    View Document
                    <span className="loc-dropdown-chevron">
                      {locDropdownOpen ? '\u25B2' : '\u25BC'}
                    </span>
                  </button>
                  {locDropdownOpen && (
                    <div className="loc-dropdown-menu">
                      {locDocuments.map((doc, index) => (
                        <button
                          key={doc.blobName}
                          className="loc-dropdown-item"
                          onClick={() => handleOpenLocDocument(doc)}
                        >
                          <span className="loc-dropdown-item-date">
                            {doc.lastModified
                              ? new Date(doc.lastModified).toLocaleDateString('en-GB', {
                                  day: 'numeric', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })
                              : 'Date unknown'}
                          </span>
                          {index === 0 && (
                            <span className="loc-dropdown-item-badge">Latest</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Correspondence Section */}
      <div className="files-section">
        <div className="correspondence-section-header">
          <h4>Correspondence</h4>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn-primary view-all-btn"
              onClick={() => setShowCorrespondenceViewer(true)}
              disabled={!correspondence || correspondence.length === 0}
              title="View full correspondence timeline with details"
            >
              📋 View Correspondence Timeline
            </button>
            <button
              className="btn-primary record-call-btn"
              onClick={() => setShowCallRecorder(true)}
            >
              📞 Record Call
            </button>
          </div>
        </div>
        {correspondence && correspondence.length > 0 ? (
          <div className="correspondence-summary">
            <p className="correspondence-count">
              {correspondence.length} correspondence item{correspondence.length === 1 ? '' : 's'} recorded
            </p>
            <p className="correspondence-hint">
              Click "View Correspondence Timeline" to see all emails, letters, and phone calls
            </p>
          </div>
        ) : (
          <div className="no-files">No correspondence yet</div>
        )}
      </div>

      {/* Existing Files Section */}
      <div className="files-section">
        <h4>Uploaded Files</h4>
        {loading ? (
          <div className="loading">Loading files...</div>
        ) : files.length === 0 ? (
          <div className="no-files">No files uploaded yet</div>
        ) : (
          <div className="files-list">
            {files.map((file) => (
              <div key={file.blob_name} className="file-item">
                <div className="file-icon">{getFileIcon(file.content_type)}</div>
                <div className="file-details">
                  <div className="file-name">
                    <button
                      onClick={() => handleViewFile(file)}
                      style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                    >
                      {file.filename}
                    </button>
                  </div>
                  <div className="file-meta">
                    {formatFileSize(file.size)} • Uploaded by {file.uploaded_by} • {formatDate(file.uploaded_at)}
                    {file.description && <span className="file-description"> • {file.description}</span>}
                  </div>
                </div>
                {(isAdmin || file.uploaded_by === currentUserEmail) && (
                  <button
                    className="delete-button"
                    onClick={() => handleDelete(file)}
                    title="Delete file"
                  >
                    🗑️
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Email Viewer Modal */}
      {selectedEmail && (
        <EmailViewer
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
        />
      )}

      {/* Call Recorder Modal */}
      <CallRecorderModal
        isOpen={showCallRecorder}
        onClose={() => setShowCallRecorder(false)}
        onSave={handleSaveCall}
        initialFrom={currentUserName}
        initialTo={claim ? `${claim.firstName || ''} ${claim.lastName || ''}`.trim() || 'Claimant' : 'Claimant'}
        clientPhoneNumber={claim?.phoneNumber}
        userFirstname={userFirstname}
        userLastname={userLastname}
        claimId={claimId}
      />

      {/* Correspondence Viewer Modal */}
      {correspondence && correspondence.length > 0 && (
        <CorrespondenceViewer
          isOpen={showCorrespondenceViewer}
          onClose={() => setShowCorrespondenceViewer(false)}
          correspondence={correspondence}
          claim={claim}
          onPdfOpen={handlePdfOpenFromCorrespondence}
        />
      )}

      {/* PDF Viewer Drawer */}
      {selectedPdf && (
        <PDFViewerDrawer
          open={pdfViewerOpen}
          onClose={handlePdfClose}
          pdfUrl={selectedPdf.url}
          fileName={selectedPdf.fileName}
        />
      )}

      {/* Doc Viewer Drawer (for .docx LOC documents) */}
      {selectedDoc && (
        <DocViewerDrawer
          open={docViewerOpen}
          onClose={() => {
            setDocViewerOpen(false);
            setSelectedDoc(null);
          }}
          docUrl={selectedDoc.url}
          fileName={selectedDoc.fileName}
        />
      )}

      {/* Upload Dialog - prompts for units, description, and metadata */}
      {showUploadDialog && (
        <div className="modal-overlay" onClick={handleUploadCancel}>
          <div className="modal-content upload-dialog" onClick={e => e.stopPropagation()}>
            <h3>Upload File{pendingFiles.length > 1 ? 's' : ''}</h3>
            <p className="upload-dialog-files">
              {pendingFiles.map(f => f.name).join(', ')}
            </p>
            {isEmailFile && (
              <p className="upload-dialog-hint">Email metadata will be extracted automatically from the file.</p>
            )}
            {!isEmailFile && (
              <>
                <div className="form-group">
                  <label htmlFor="upload-sender">From</label>
                  <input
                    id="upload-sender"
                    type="text"
                    value={uploadSender}
                    onChange={e => setUploadSender(e.target.value)}
                    placeholder="e.g. lender@bank.com"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="upload-recipient">To</label>
                  <input
                    id="upload-recipient"
                    type="text"
                    value={uploadRecipient}
                    onChange={e => setUploadRecipient(e.target.value)}
                    placeholder="e.g. client@email.com"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="upload-date">Date</label>
                  <input
                    id="upload-date"
                    type="datetime-local"
                    value={uploadDate}
                    onChange={e => setUploadDate(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="upload-direction">Direction</label>
                  <select
                    id="upload-direction"
                    value={uploadDirection}
                    onChange={e => setUploadDirection(e.target.value as 'sent' | 'received')}
                    className="form-input"
                  >
                    <option value="received">Received</option>
                    <option value="sent">Sent</option>
                  </select>
                </div>
              </>
            )}
            <div className="form-group">
              <label htmlFor="upload-units">Units</label>
              <input
                id="upload-units"
                type="number"
                min="0"
                value={uploadUnits}
                onChange={e => setUploadUnits(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="upload-description">Description</label>
              <input
                id="upload-description"
                type="text"
                value={uploadDescription}
                onChange={e => setUploadDescription(e.target.value)}
                placeholder="e.g. Email from lender regarding settlement"
                className="form-input"
                autoFocus
              />
            </div>
            {error && <p className="upload-dialog-error">{error}</p>}
            <div className="upload-dialog-actions">
              <button className="btn-secondary" onClick={handleUploadCancel}>Cancel</button>
              <button className="btn-primary" onClick={handleUploadConfirm}>Upload</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 
